import { useApolloClient } from '@apollo/client'
import type { BestPracticeData } from '@deltek/specification-client/core/Models/bestPractice'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { useCallback, useEffect, useRef, useState } from 'react'

interface UseBestPracticeDetailsState {
    data: BestPracticeData | null
    loading: boolean
    error: boolean
}

interface UseBestPracticeDetailsReturn extends UseBestPracticeDetailsState {
    /** Manually fires the fetch. Pass force=true to bypass the stale-data guard. */
    fetch: (force?: boolean) => void
    /** Clears state. */
    reset: () => void
}

interface UseBestPracticeDetailsOptions {
    /** When true, fires fetch on mount and again whenever refetchTick changes. */
    fetchOnMount?: boolean
    /** Bump (e.g. via `requestBPDetailsRefetch`) to force a fresh fetch. */
    refetchTick?: number
}

const initialState: UseBestPracticeDetailsState = {
    data: null,
    loading: false,
    error: false,
}

export const useBestPracticeDetails = (
    contentItemId: string | undefined,
    projectId: string | undefined,
    {
        fetchOnMount = false,
        refetchTick,
    }: UseBestPracticeDetailsOptions = {}
): UseBestPracticeDetailsReturn => {
    const apolloClient = useApolloClient()
    const [state, setState] = useState<UseBestPracticeDetailsState>(
        initialState
    )
    // Ref keeps fetch stable without adding `state` to its dependency array,
    // which would cause infinite re-renders.
    const stateRef = useRef(state)
    stateRef.current = state

    const fetch = useCallback((force = false) => {
        if (!contentItemId || !projectId) return
        // Skip if data is already loaded and this is a manual (non-forced) call.
        // Tick-driven refetches pass force=true to bypass this guard.
        if (!force && stateRef.current.data !== null && !stateRef.current.loading) return

        setState({ data: null, loading: true, error: false })
        apolloClient
            .query<{ getBestPractice: BestPracticeData | null }>({
                query: BestPracticeQuery,
                variables: { parentId: contentItemId, projectId },
                fetchPolicy: 'no-cache',
            })
            .then(({ data }) => {
                setState({
                    data: data?.getBestPractice ?? null,
                    loading: false,
                    error: false,
                })
            })
            .catch(() => {
                setState({ data: null, loading: false, error: true })
            })
    }, [apolloClient, contentItemId, projectId])

    const reset = useCallback(() => setState(initialState), [])

    useEffect(() => {
        // Tick-driven refetches bypass the stale-data guard; initial mount fetch does not.
        if (fetchOnMount) fetch(refetchTick !== undefined && refetchTick > 0)
    }, [fetchOnMount, fetch, refetchTick])

    return { ...state, fetch, reset }
}
